"use client"

import { useRef } from "react"
import { motion, useInView } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Shield, Zap, LineChart, Brain, Layers, RefreshCw, Cpu } from "lucide-react"

export const FeaturesGrid = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.2 })

  const features = [
    {
      title: "Quantum-Speed Analysis",
      description:
        "Process millions of market data points in microseconds using our distributed AI infrastructure to identify alpha opportunities before they disappear.",
      icon: <Zap className="h-10 w-10 text-blue-400" />,
    },
    {
      title: "Adaptive Risk Shield™",
      description:
        "Dynamic risk management system that automatically adjusts position sizing and hedging strategies based on real-time market volatility.",
      icon: <Shield className="h-10 w-10 text-blue-400" />,
    },
    {
      title: "Neural Portfolio Optimization",
      description:
        "Advanced deep learning models continuously analyze your portfolio to maximize returns while maintaining your desired risk parameters.",
      icon: <Brain className="h-10 w-10 text-blue-400" />,
    },
    {
      title: "Multi-Factor Signal Generation",
      description:
        "Combines technical, fundamental, sentiment, and alternative data sources to generate high-confidence trading signals.",
      icon: <Layers className="h-10 w-10 text-blue-400" />,
    },
    {
      title: "Adaptive Market Regime Detection",
      description:
        "Automatically identifies changing market conditions and adjusts strategies to maintain performance across different environments.",
      icon: <RefreshCw className="h-10 w-10 text-blue-400" />,
    },
    {
      title: "Institutional-Grade Backtesting",
      description:
        "Rigorously test strategies against decades of market data with realistic slippage, fees, and market impact models.",
      icon: <LineChart className="h-10 w-10 text-blue-400" />,
    },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" ref={ref}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-16"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
          Institutional-Grade <span className="text-blue-500">Trading Intelligence</span>
        </h2>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
          Our proprietary AI models combine deep learning with high-frequency market data to deliver sophisticated
          trading strategies previously reserved for hedge funds.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card className="bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:bg-gray-800/70 transition-all rounded-xl h-full hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/5 group">
              <CardContent className="p-6">
                <div className="mb-4 p-3 rounded-lg bg-gray-700/50 w-fit group-hover:bg-blue-500/20 transition-colors duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-400 transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-gray-400">{feature.description}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Advanced AI visualization - adjust gradient overlay */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="mt-16 p-8 rounded-xl bg-gray-800/30 border border-gray-700 backdrop-blur-sm relative overflow-hidden"
      >
        {/* Make the gradient overlay more subtle to keep visualization elements vibrant */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-gray-900/30 to-transparent pointer-events-none" />
        
        <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
          <div className="w-full md:w-1/2">
            <div className="p-4 rounded-lg bg-gray-800/80 border border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <Cpu className="h-6 w-6 text-blue-400" />
                <h3 className="text-xl font-semibold text-white">Proprietary Neural Architecture</h3>
              </div>
              <p className="text-gray-400 mb-4">
                Our custom-designed neural networks are specifically optimized for financial time series data, with
                specialized layers for detecting market regimes, volatility clustering, and non-linear patterns.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 rounded-lg bg-gray-700/50">
                  <div className="text-blue-400 font-medium">Attention Layers</div>
                  <div className="text-gray-300">12</div>
                </div>
                <div className="p-3 rounded-lg bg-gray-700/50">
                  <div className="text-blue-400 font-medium">Training Data</div>
                  <div className="text-gray-300">25+ Years</div>
                </div>
                <div className="p-3 rounded-lg bg-gray-700/50">
                  <div className="text-blue-400 font-medium">Model Updates</div>
                  <div className="text-gray-300">Daily</div>
                </div>
                <div className="p-3 rounded-lg bg-gray-700/50">
                  <div className="text-blue-400 font-medium">Data Sources</div>
                  <div className="text-gray-300">50+</div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full md:w-1/2 h-64 rounded-lg bg-gray-800/80 border border-gray-700 overflow-hidden relative">
            {/* Neural network visualization */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-full p-4 flex items-center justify-center">
                <svg width="100%" height="100%" viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Input layer */}
                  {[0, 1, 2, 3, 4].map((i) => (
                    <g key={`input-${i}`}>
                      <circle cx="50" cy={40 + i * 30} r="8" fill="rgba(59, 130, 246, 0.7)" />
                      {/* Connections to hidden layer 1 */}
                      {[0, 1, 2, 3].map((j) => (
                        <line
                          key={`input-h1-${i}-${j}`}
                          x1="58"
                          y1={40 + i * 30}
                          x2="142"
                          y2={50 + j * 30}
                          stroke="rgba(59, 130, 246, 0.2)"
                          strokeWidth="1"
                        />
                      ))}
                    </g>
                  ))}

                  {/* Hidden layer 1 */}
                  {[0, 1, 2, 3].map((i) => (
                    <g key={`h1-${i}`}>
                      <circle cx="150" cy={50 + i * 30} r="8" fill="rgba(99, 102, 241, 0.7)" />
                      {/* Connections to hidden layer 2 */}
                      {[0, 1, 2].map((j) => (
                        <line
                          key={`h1-h2-${i}-${j}`}
                          x1="158"
                          y1={50 + i * 30}
                          x2="242"
                          y2={60 + j * 30}
                          stroke="rgba(99, 102, 241, 0.2)"
                          strokeWidth="1"
                        />
                      ))}
                    </g>
                  ))}

                  {/* Hidden layer 2 */}
                  {[0, 1, 2].map((i) => (
                    <g key={`h2-${i}`}>
                      <circle cx="250" cy={60 + i * 30} r="8" fill="rgba(139, 92, 246, 0.7)" />
                      {/* Connections to output layer */}
                      {[0, 1].map((j) => (
                        <line
                          key={`h2-out-${i}-${j}`}
                          x1="258"
                          y1={60 + i * 30}
                          x2="342"
                          y2={70 + j * 30}
                          stroke="rgba(139, 92, 246, 0.2)"
                          strokeWidth="1"
                        />
                      ))}
                    </g>
                  ))}

                  {/* Output layer */}
                  {[0, 1].map((i) => (
                    <g key={`output-${i}`}>
                      <circle cx="350" cy={70 + i * 30} r="8" fill="rgba(16, 185, 129, 0.7)" />
                    </g>
                  ))}

                  {/* Animated data particles */}
                  <circle r="3" fill="rgba(59, 130, 246, 0.9)">
                    <animateMotion path="M58,40 L142,50" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <circle r="3" fill="rgba(59, 130, 246, 0.9)">
                    <animateMotion path="M58,100 L142,110" dur="1.8s" repeatCount="indefinite" />
                  </circle>
                  <circle r="3" fill="rgba(99, 102, 241, 0.9)">
                    <animateMotion path="M158,50 L242,60" dur="1.2s" repeatCount="indefinite" />
                  </circle>
                  <circle r="3" fill="rgba(139, 92, 246, 0.9)">
                    <animateMotion path="M258,60 L342,70" dur="1s" repeatCount="indefinite" />
                  </circle>
                </svg>
              </div>
            </div>

            {/* Layer labels */}
            <div className="absolute bottom-2 left-0 right-0 flex justify-around text-xs text-gray-400">
              <div>Market Data</div>
              <div>Feature Extraction</div>
              <div>Pattern Recognition</div>
              <div>Signal Generation</div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
